/*
 * 시험일정 상세 모달 (공용)
 *  - 시험일정 목록(ScheduleListPage)·대시보드 상태별 목록에서 동일하게 사용
 *  - 기본정보 + 진행률 + 가로 타임라인 + 파트너확인
 */
import React, { useEffect, useState } from 'react';
import {
  Card, Tag, Typography, Button, Row, Col, Modal, Descriptions, Progress, Spin, message,
} from 'antd';
import { CheckCircleOutlined, ExperimentOutlined, EnvironmentOutlined, MessageOutlined } from '@ant-design/icons';
import { partnerAPI } from '../services/api';
import RequestTypeBadge from './RequestTypeBadge';
import InquirySection from './InquirySection';
import { fmtDate } from '../utils/date';

const { Text } = Typography;

// 하드코딩 fallback — 마스터 상태코드 색상 미제공 시 사용
export const STATUS_COLORS_FALLBACK: Record<string, string> = {
  '시작전': '#94a3b8',
  '처리완료': '#3b82f6', '조사완료': '#f59e0b', '성적서작성중': '#8b5cf6',
  '성적서완료': '#10b981', '발급완료': '#0ea5e9', '최종종료': '#64748b',
};

const TYPE_CFG = {
  '처리':    { color: '#f59e0b', label: '처리' },
  '약효조사': { color: '#10b981', label: '약효조사' },
  '약해조사': { color: '#8b5cf6', label: '약해조사' },
} as const;
export type EventType = keyof typeof TYPE_CFG;

export interface TimelineEvent {
  key: string;
  date: string | null;
  type: EventType;
  round: number;
  completed: boolean;
}

export const buildChronologicalEvents = (detailData: any): { events: TimelineEvent[]; completedCount: number } => {
  // 분모 = 실제 등록된 회차 수 (시험건별 상이 — 고정 최소치/가상 회차 없음)
  // 완료 판정 = ISO 날짜(YYYY-MM-DD)이면서 오늘 이하; 미래일·자유입력 텍스트("파종전" 등)는 예정
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const isDone = (date: any) =>
    typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date) && date.slice(0, 10) <= today;

  const treatments = [...(detailData.treatments || [])].sort((a: any, b: any) => a.round_no - b.round_no);
  const allSurveys  = [...(detailData.surveys   || [])].sort((a: any, b: any) => a.round_no - b.round_no);
  const yakhoSurveys = allSurveys.filter((sv: any) => sv.survey_type === '약효' || !sv.survey_type);
  const yakheSurveys = allSurveys.filter((sv: any) => sv.survey_type === '약해');

  const raw: TimelineEvent[] = [
    ...treatments.map((t: any)    => ({ key: `t${t.round_no}`,   date: t.treatment_date || null, type: '처리'    as EventType, round: t.round_no,  completed: isDone(t.treatment_date) })),
    ...yakhoSurveys.map((sv: any) => ({ key: `yh${sv.round_no}`, date: sv.survey_date   || null, type: '약효조사' as EventType, round: sv.round_no, completed: isDone(sv.survey_date) })),
    ...yakheSurveys.map((sv: any) => ({ key: `yk${sv.round_no}`, date: sv.survey_date   || null, type: '약해조사' as EventType, round: sv.round_no, completed: isDone(sv.survey_date) })),
  ];
  const completed = raw.filter(e => e.completed).sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  const pending   = raw.filter(e => !e.completed).sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  return { events: [...completed, ...pending], completedCount: completed.length };
};

// 이벤트 열 너비 / 커넥터 너비 — 분리 고정으로 간격 정밀 제어
const TL_ITEM = 56;  // px
const TL_CONN = 14;  // px
// 날짜 행 18px + 도트 행 22px → 도트 중심 = 29px → 커넥터 marginTop = 28px
const TL_CONN_TOP = 28;

const HorizontalTimeline: React.FC<{ detailData: any }> = ({ detailData }) => {
  const { events, completedCount } = buildChronologicalEvents(detailData);

  return (
    <div>
      <div style={{ overflowX: 'auto', paddingBottom: 2 }}>
        <div style={{ display: 'inline-flex', alignItems: 'flex-start', minWidth: 0 }}>
          {events.map((ev, idx) => {
            const isLast = idx === events.length - 1;
            const isPendingNext = !ev.completed && idx === completedCount;
            const isFirstPending = idx === completedCount;  // 미완료 시작점
            const cfg = TYPE_CFG[ev.type];

            const dotSize   = ev.completed ? 15 : isPendingNext ? 12 : 10;
            const dotColor  = ev.completed ? cfg.color : isPendingNext ? '#f97316' : '#d1d5db';
            const connColor = ev.completed ? `${cfg.color}66` : '#e8ecf0';

            return (
              <React.Fragment key={ev.key}>
                {/* 완료→미완료 경계 구분선 */}
                {isFirstPending && completedCount > 0 && (
                  <div style={{
                    width: 1, flexShrink: 0, marginTop: 4, marginBottom: 0,
                    background: '#e2e8f0', alignSelf: 'stretch',
                    marginLeft: 2, marginRight: 2,
                  }} />
                )}

                {/* 이벤트 열 */}
                <div style={{ width: TL_ITEM, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {/* 날짜 */}
                  <div style={{
                    height: 18, lineHeight: '18px', fontSize: 10, fontWeight: ev.completed ? 600 : 400,
                    color: ev.completed ? '#475569' : isPendingNext ? '#fb923c' : '#d1d5db',
                    textAlign: 'center', width: '100%',
                  }}>
                    {ev.date ? ev.date.replace(/\d{4}-(\d{2})-(\d{2})/, '$1/$2') : '—'}
                  </div>
                  {/* 도트 */}
                  <div style={{ height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{
                      width: dotSize, height: dotSize, borderRadius: '50%', background: dotColor,
                      boxShadow: isPendingNext
                        ? '0 0 0 4px rgba(249,115,22,0.18)'
                        : ev.completed ? `0 0 0 3px ${cfg.color}28` : 'none',
                    }} />
                  </div>
                  {/* 라벨 */}
                  <div style={{ textAlign: 'center', marginTop: 4 }}>
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 8,
                      display: 'inline-block', whiteSpace: 'nowrap',
                      background: ev.completed ? cfg.color : isPendingNext ? '#fff7ed' : '#f8fafc',
                      color: ev.completed ? '#fff' : isPendingNext ? '#f97316' : '#b0bac6',
                      border: `1px solid ${ev.completed ? cfg.color : isPendingNext ? '#fed7aa' : '#e2e8f0'}`,
                    }}>
                      {cfg.label}
                    </span>
                    <div style={{
                      fontSize: 10, marginTop: 2, fontWeight: ev.completed ? 600 : 400,
                      color: ev.completed ? '#334155' : isPendingNext ? '#f97316' : '#b0bac6',
                    }}>
                      {ev.round}차
                    </div>
                    {!ev.completed && (
                      <div style={{ fontSize: 9, marginTop: 1, color: isPendingNext ? '#f97316' : '#cbd5e1' }}>
                        {isPendingNext ? '진행예정' : '미정'}
                      </div>
                    )}
                  </div>
                </div>

                {/* 커넥터 — 고정 너비, 도트 중심에 정렬 */}
                {!isLast && (
                  <div style={{
                    width: TL_CONN, height: 2, flexShrink: 0,
                    background: connColor,
                    marginTop: TL_CONN_TOP,
                  }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* 범례 */}
      <div style={{ display: 'flex', gap: 12, marginTop: 10, paddingTop: 8, borderTop: '1px solid #f1f5f9', flexWrap: 'wrap', justifyContent: 'center' }}>
        {([
          { color: '#f59e0b', label: '처리' },
          { color: '#10b981', label: '약효조사' },
          { color: '#8b5cf6', label: '약해조사' },
          { color: '#f97316', label: '진행예정', pulse: true },
          { color: '#d1d5db', label: '미정' },
        ] as { color: string; label: string; pulse?: boolean }[]).map(item => (
          <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 9, height: 9, borderRadius: '50%', background: item.color, flexShrink: 0,
              boxShadow: item.pulse ? '0 0 0 3px rgba(249,115,22,0.2)' : 'none' }} />
            <span style={{ fontSize: 10, color: '#64748b' }}>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

interface ScheduleDetailModalProps {
  open: boolean;
  scheduleId: number | null;
  /** 상세 로딩 전 모달 제목에 즉시 표시할 목록 레코드 (title/test_number/status) */
  titleRecord?: { title?: string; test_number?: string; status?: string } | null;
  /** 마스터 상태코드 색상 맵 — 미전달 시 fallback 색상 사용 */
  statusColorMap?: Record<string, string>;
  onClose: () => void;
  /** 파트너확인 등으로 데이터가 변경됐을 때 호출 (목록/대시보드 갱신용) */
  onChanged?: () => void;
}

const ScheduleDetailModal: React.FC<ScheduleDetailModalProps> = ({
  open, scheduleId, titleRecord, statusColorMap, onClose, onChanged,
}) => {
  const [detailData, setDetailData] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // 문의/요청 섹션 — 기본은 접힘, 미확인 답변이 있으면 자동 펼침
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [inquiryStat, setInquiryStat] = useState<{ total: number; unread: number }>({ total: 0, unread: 0 });

  const colorMap = statusColorMap || STATUS_COLORS_FALLBACK;

  const loadInquiryStat = React.useCallback(() => {
    if (!scheduleId) return;
    partnerAPI.inquiries({ schedule_id: scheduleId })
      .then(({ data }) => {
        const list = data || [];
        const unread = list.filter((x: any) => x.partner_unread).length;
        setInquiryStat({ total: list.length, unread });
        if (unread > 0) setInquiryOpen(true);
      })
      .catch(() => setInquiryStat({ total: 0, unread: 0 }));
  }, [scheduleId]);

  useEffect(() => {
    if (!open || !scheduleId) return;
    setDetailData(null);
    setDetailLoading(true);
    setInquiryOpen(false);
    setInquiryStat({ total: 0, unread: 0 });
    loadInquiryStat();
    partnerAPI.scheduleDetail(scheduleId)
      .then(res => setDetailData(res.data))
      .catch(() => {
        message.error('상세 정보를 불러오지 못했습니다.');
        onClose();
      })
      .finally(() => setDetailLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scheduleId]);

  const handleConfirm = async () => {
    if (!detailData) return;
    setConfirming(true);
    try {
      await partnerAPI.confirmSchedule(detailData.id);
      message.success('파트너확인이 완료되었습니다.');
      const res = await partnerAPI.scheduleDetail(detailData.id);
      setDetailData(res.data);
      onChanged?.();
    } catch {
      message.error('파트너확인 처리 중 오류가 발생했습니다.');
    } finally {
      setConfirming(false);
    }
  };

  const renderDetailContent = () => {
    if (detailLoading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
    if (!detailData) return null;

    // Progress stats
    const { events } = buildChronologicalEvents(detailData);
    const tEvents  = events.filter(e => e.type === '처리');
    const yhEvents = events.filter(e => e.type === '약효조사');
    const ykEvents = events.filter(e => e.type === '약해조사');
    const filledT  = tEvents.filter(e => e.completed).length;
    const filledYh = yhEvents.filter(e => e.completed).length;
    const filledYk = ykEvents.filter(e => e.completed).length;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Basic info — 긴 값(시험번호·의뢰사·주소)은 span 2, 짧은 값끼리 2열 배치로 1줄 유지 */}
        <Card size="small" style={{ borderRadius: 8 }}>
          <Descriptions
            column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }}
            size="small"
            bordered
            styles={{
              label: { whiteSpace: 'nowrap', width: 100, fontSize: 12 },
              content: { fontSize: 13 },
            }}
          >
            <Descriptions.Item label="시험번호" span={2}>
              <Text strong style={{ color: '#92400e' }}>{detailData.test_number}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="시험년도">{detailData.test_year ? `${detailData.test_year}년` : '-'}</Descriptions.Item>
            <Descriptions.Item label="의뢰구분"><RequestTypeBadge value={detailData.request_type} /></Descriptions.Item>
            <Descriptions.Item label="시험구분">{detailData.category_name}</Descriptions.Item>
            <Descriptions.Item label="시험장소">{detailData.location_name}</Descriptions.Item>
            <Descriptions.Item label="개시일">{detailData.start_date}</Descriptions.Item>
            <Descriptions.Item label="포장주">{detailData.field_owner_name}</Descriptions.Item>
            <Descriptions.Item label="성적서발급일">{detailData.report_date || '-'}</Descriptions.Item>
            <Descriptions.Item label="의뢰사" span={2}>
              {((detailData as any).companies || []).length > 0
                ? ((detailData as any).companies as any[]).map((c: any, i: number) => (
                    <span key={c.id || i} style={{ marginRight: 6, whiteSpace: 'nowrap' }}>
                      {c.code && <Tag color={c.is_primary ? 'orange' : 'default'} style={{ fontWeight: 600 }}>{c.code}</Tag>}
                      {c.name}
                    </span>
                  ))
                : (detailData as any).company_code
                  ? <><Tag color="orange" style={{ fontWeight: 600 }}>{(detailData as any).company_code}</Tag> {detailData.company_name}</>
                  : detailData.company_name}
            </Descriptions.Item>
            {detailData.field_address && (
              <Descriptions.Item label="시험포장주소" span={2}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', verticalAlign: 'middle' }}>
                  <span>{detailData.field_address}</span>
                  <a
                    href={`https://map.naver.com/p/search/${encodeURIComponent(detailData.field_address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0,
                      fontSize: 11, fontWeight: 600, color: '#03c75a',
                      border: '1px solid #03c75a', borderRadius: 10, padding: '0 7px', lineHeight: '18px',
                    }}
                  >
                    <EnvironmentOutlined style={{ fontSize: 10 }} /> 네이버지도
                  </a>
                </span>
              </Descriptions.Item>
            )}
          </Descriptions>
          {/* 성적서사진유무는 내부 관리 항목 — 파트너 화면에서는 노출하지 않는다 (2026-08) */}
        </Card>

        {/* Progress */}
        <Card size="small" style={{ borderRadius: 8, background: '#fffbeb' }}>
          <Row gutter={16}>
            <Col span={8}>
              <div style={{ fontSize: 11, color: '#92400e', marginBottom: 4, textAlign: 'center' }}>처리 진행률</div>
              <Progress percent={tEvents.length ? Math.round((filledT / tEvents.length) * 100) : 0}
                size="small" strokeColor="#f59e0b" format={() => `${filledT}/${tEvents.length}`} />
            </Col>
            <Col span={8}>
              <div style={{ fontSize: 11, color: '#92400e', marginBottom: 4, textAlign: 'center' }}>약효조사 진행률</div>
              <Progress percent={yhEvents.length ? Math.round((filledYh / yhEvents.length) * 100) : 0}
                size="small" strokeColor="#10b981" format={() => `${filledYh}/${yhEvents.length}`} />
            </Col>
            <Col span={8}>
              <div style={{ fontSize: 11, color: '#92400e', marginBottom: 4, textAlign: 'center' }}>약해조사 진행률</div>
              <Progress percent={ykEvents.length ? Math.round((filledYk / ykEvents.length) * 100) : 0}
                size="small" strokeColor="#8b5cf6" format={() => `${filledYk}/${ykEvents.length}`} />
            </Col>
          </Row>
        </Card>

        {/* Horizontal Timeline */}
        <Card size="small" title="시험 진행 타임라인" style={{ borderRadius: 8 }}>
          <HorizontalTimeline detailData={detailData} />
        </Card>

        {/* 문의/요청 — 접이식 섹션 (미확인 답변 있으면 자동 펼침) */}
        <Card
          size="small"
          style={{ borderRadius: 8, borderColor: inquiryStat.unread > 0 ? '#fbbf24' : undefined }}
          styles={{ body: { padding: inquiryOpen ? 12 : 0 } }}
          title={
            <div
              onClick={() => setInquiryOpen(o => !o)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
            >
              <MessageOutlined style={{ color: '#d97706' }} />
              <span style={{ fontWeight: 700, color: '#92400e', fontSize: 13 }}>문의 / 요청</span>
              {inquiryStat.total > 0 && (
                <Tag style={{ margin: 0, fontSize: 11 }}>{inquiryStat.total}건</Tag>
              )}
              {inquiryStat.unread > 0 && (
                <Tag color="orange" style={{ margin: 0, fontSize: 11, fontWeight: 700 }}>
                  새 답변 {inquiryStat.unread}
                </Tag>
              )}
              <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8' }}>
                {inquiryOpen ? '접기 ▲' : (inquiryStat.total > 0 ? '펼치기 ▼' : '이 시험에 대해 문의하기 ▼')}
              </span>
            </div>
          }
        >
          {inquiryOpen && (
            <InquirySection
              key={scheduleId ?? 0}
              bare
              scheduleId={scheduleId ?? undefined}
              scheduleLabel={`${detailData.test_number || ''} · ${detailData.title || ''}`}
              onChanged={loadInquiryStat}
            />
          )}
        </Card>

        {/* Partner confirm status */}
        {detailData.partner_confirmed_at && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 14px', borderRadius: 8,
            background: '#f0fdf4', border: '1px solid #bbf7d0',
          }}>
            <CheckCircleOutlined style={{ color: '#10b981', fontSize: 16 }} />
            <span style={{ fontSize: 12, color: '#15803d' }}>
              파트너확인 완료 — {detailData.partner_confirmed_by}
              {detailData.partner_confirmed_at && ` · ${fmtDate(detailData.partner_confirmed_at)}`}
            </span>
          </div>
        )}
      </div>
    );
  };

  const modalTitleRecord = detailData || titleRecord;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      maskClosable={false}
      width={720}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingRight: 20 }}>
          <ExperimentOutlined style={{ color: '#f59e0b', fontSize: 18, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {modalTitleRecord?.title || ''}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 400 }}>
              {modalTitleRecord?.test_number || ''}
            </div>
          </div>
          {modalTitleRecord?.status && (
            <Tag
              color={colorMap[modalTitleRecord.status] || 'default'}
              style={{ fontSize: 12, flexShrink: 0, margin: 0 }}
            >
              {modalTitleRecord.status}
            </Tag>
          )}
        </div>
      }
      footer={
        detailData && !detailLoading ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {!detailData.partner_confirmed_at && (
                <Button
                  type="primary" icon={<CheckCircleOutlined />}
                  loading={confirming} onClick={handleConfirm}
                  style={{ background: '#10b981', borderColor: '#10b981' }}
                >
                  파트너확인
                </Button>
              )}
              {detailData.partner_confirmed_at && (
                <span style={{ fontSize: 12, color: '#15803d' }}>
                  <CheckCircleOutlined style={{ marginRight: 4 }} />파트너확인 완료
                </span>
              )}
              <Button
                icon={<MessageOutlined />}
                onClick={() => {
                  setInquiryOpen(true);
                  // 섹션이 화면 아래에 있으므로 본문 최하단으로 스크롤
                  setTimeout(() => {
                    const body = document.querySelector('.ant-modal-body');
                    if (body) body.scrollTo({ top: body.scrollHeight, behavior: 'smooth' });
                  }, 120);
                }}
                style={{ marginLeft: 8, borderColor: '#f59e0b', color: '#b45309' }}
              >
                문의 / 요청
                {inquiryStat.unread > 0 && (
                  <Tag color="orange" style={{ marginLeft: 6, marginRight: 0, fontSize: 10, fontWeight: 700 }}>
                    {inquiryStat.unread}
                  </Tag>
                )}
              </Button>
            </div>
            <Button onClick={onClose}>닫기</Button>
          </div>
        ) : (
          <Button onClick={onClose}>닫기</Button>
        )
      }
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
    >
      {renderDetailContent()}
    </Modal>
  );
};

export default ScheduleDetailModal;
